import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { X, Loader2, Download, Video, BookImage, Tv2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";

interface BringToLifeModalProps {
  imageUrl: string;
  imageName: string;
  onClose: () => void;
  onAnimate: (imageUrl: string, motionBucket: number, prompt: string) => Promise<string | null>;
  onSaveToBoard: (videoUrl: string, name: string) => void;
  onSetChatBackground: (videoUrl: string) => void;
}

const MOTION_PRESETS = [
  { max: 3,  label: "Imperceptible", prompt: "Barely perceptible, living photograph effect. Micro breathing motion only. No camera movement." },
  { max: 7,  label: "Micro",         prompt: "Extremely subtle micro-motion. Gentle atmospheric shimmer, slight depth breathing. Minimal movement." },
  { max: 12, label: "Ultra-subtle",  prompt: "Very soft natural motion. Gentle environmental movement, subtle light shifts. Calm and serene." },
  { max: 20, label: "Subtle",        prompt: "Soft natural motion. Gentle breeze, flowing elements, light environmental animation." },
  { max: 30, label: "Gentle",        prompt: "Moderate natural motion. Visible movement with dynamic elements, lively and engaging scene." },
];

const getPreset = (val: number) =>
  MOTION_PRESETS.find((m) => val <= m.max) ?? MOTION_PRESETS[MOTION_PRESETS.length - 1];

export const BringToLifeModal: React.FC<BringToLifeModalProps> = ({
  imageUrl,
  imageName,
  onClose,
  onAnimate,
  onSaveToBoard,
  onSetChatBackground,
}) => {
  const [motionBucket, setMotionBucket] = useState(5);
  const [motionPrompt, setMotionPrompt] = useState(getPreset(5).prompt);
  const [isAnimating, setIsAnimating] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const handleSliderChange = useCallback((val: number) => {
    setMotionBucket(val);
    setMotionPrompt(getPreset(val).prompt);
  }, []);

  useEffect(() => {
    if (!isAnimating) { setElapsedSeconds(0); return; }
    const interval = setInterval(() => setElapsedSeconds((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [isAnimating]);

  const handleAnimate = async () => {
    setIsAnimating(true);
    setError(null);
    try {
      const result = await onAnimate(imageUrl, motionBucket, motionPrompt);
      if (result) {
        setVideoUrl(result);
      } else {
        setError("Video generation failed. Please try again.");
      }
    } catch (e: any) {
      setError(e.message || "Video generation failed.");
    } finally {
      setIsAnimating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-3"
      onClick={isAnimating ? undefined : onClose}
    >
      <motion.div
        initial={{ scale: 0.94, opacity: 0, y: 10 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.94, opacity: 0, y: 10 }}
        transition={{ type: "spring", damping: 24, stiffness: 280 }}
        className="cosmic-card w-full max-w-md flex flex-col overflow-hidden"
        style={{ maxHeight: "92vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-2">
            <Video className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold text-foreground">Bring to Life</h3>
            <span className="text-xs text-muted-foreground truncate max-w-[160px]">{imageName}</span>
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} disabled={isAnimating}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {videoUrl ? (
          /* ── Result view ── */
          <div className="flex flex-col gap-4 p-5">
            <video
              src={videoUrl}
              autoPlay
              loop
              muted
              playsInline
              controls
              className="w-full rounded-xl object-cover max-h-[55vh]"
            />
            <div className="grid grid-cols-2 gap-2">
              <Button
                className="cosmic-button gap-2"
                onClick={() => { onSaveToBoard(videoUrl, imageName); }}
              >
                <BookImage className="h-4 w-4" /> Save to Board
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => { onSetChatBackground(videoUrl); onClose(); }}
              >
                <Tv2 className="h-4 w-4" /> Use as Chat BG
              </Button>
            </div>
            <div className="flex gap-2">
              <a
                href={videoUrl}
                download="vision-alive.mp4"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1"
              >
                <Button variant="outline" className="w-full gap-2">
                  <Download className="h-4 w-4" /> Download
                </Button>
              </a>
              <Button variant="outline" onClick={() => { setVideoUrl(null); setError(null); }}>
                Try Again
              </Button>
              <Button variant="ghost" onClick={onClose}>Close</Button>
            </div>
          </div>
        ) : (
          /* ── Editor view ── */
          <div className="flex flex-col gap-6 p-5">
            {/* Image preview */}
            <img
              src={imageUrl}
              alt={imageName}
              className="w-full rounded-xl object-cover max-h-56"
            />

            {/* Motion intensity */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Motion Intensity
                </label>
                <span className="text-xs font-semibold text-primary">
                  {getPreset(motionBucket).label}
                </span>
              </div>
              <Slider
                min={1}
                max={30}
                step={1}
                value={[motionBucket]}
                onValueChange={([v]) => handleSliderChange(v)}
                disabled={isAnimating}
                className="w-full"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>Still</span>
                <span>Gentle</span>
              </div>
            </div>

            {/* Editable motion prompt */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Motion Prompt
                </label>
                <span className="text-[10px] text-muted-foreground">Edit to customise</span>
              </div>
              <Textarea
                value={motionPrompt}
                onChange={(e) => setMotionPrompt(e.target.value)}
                disabled={isAnimating}
                rows={3}
                placeholder="Describe the motion you want…"
                className="text-xs resize-none"
              />
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
                {error}
              </div>
            )}

            <Button
              className="cosmic-button w-full gap-2"
              onClick={handleAnimate}
              disabled={isAnimating}
            >
              {isAnimating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating… {elapsedSeconds > 0 ? `(${elapsedSeconds}s)` : "(~20–40s)"}
                </>
              ) : (
                <>
                  <Video className="h-4 w-4" /> Bring to Life
                </>
              )}
            </Button>
            {isAnimating && (
              <p className="text-xs text-muted-foreground text-center -mt-4">
                Kling v2.1 is animating your vision. This can take 30–90 seconds.
              </p>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};
